import { useMemo } from "react";
import { RefreshCw, Search, X } from "lucide-react";
import toast from "react-hot-toast";

import { useDevices } from "../../hooks/useDevices";
import { useDevicesStore } from "../../store/devicesStore";
import { useLiveTreeStore } from "../../store/liveTreeStore";
import { Spinner } from "../common/Spinner";
import { Button } from "../common/Button";
import { syncChannels, testConnection } from "../../api/devices";
import { formatApiError } from "../../lib/apiError";
import { DeviceNode } from "./DeviceNode";

export function DeviceTree() {
  const { isLoading, refetch } = useDevices();
  const devices = useDevicesStore((s) => s.devices);
  const channelsByDevice = useDevicesStore((s) => s.channelsByDevice);
  const setChannels = useDevicesStore((s) => s.setChannels);
  // Filtro y desplegados en un store en memoria (sin persist) para que no se
  // pierdan al cambiar de pagina y volver a Vivo.
  const query = useLiveTreeStore((s) => s.query);
  const setQuery = useLiveTreeStore((s) => s.setQuery);
  const expanded = useLiveTreeStore((s) => s.expanded);
  const toggle = useLiveTreeStore((s) => s.toggleExpanded);

  const filteredDevices = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = !q
      ? devices
      : devices.filter(
          (d) => d.name.toLowerCase().includes(q) || d.host.toLowerCase().includes(q),
        );
    return [...base].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [devices, query]);

  const handleSync = async (deviceId: number) => {
    const t = toast.loading("Sincronizando canales...");
    try {
      const res = await syncChannels(deviceId);
      const ch = res.map((r) => ({
        id: 0, device_id: deviceId, channel_number: r.channel,
        name: r.name, is_enabled: true, has_signal: true,
      }));
      setChannels(deviceId, ch);
      toast.success(`Sincronizados ${res.length} canales`, { id: t });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo al sincronizar", { id: t });
    }
  };

  const handleTest = async (deviceId: number) => {
    const t = toast.loading("Probando conexion...");
    try {
      const res = await testConnection(deviceId);
      if (res.ok) toast.success(`OK: ${res.info?.model || ""}`, { id: t });
      else toast.error(`Fallo: ${res.message}`, { id: t });
      refetch();
    } catch (e) {
      toast.error(formatApiError(e), { id: t });
    }
  };

  return (
    <div className="flex flex-col h-full bg-panel border-r border-line">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-line">
        <span className="text-xs font-semibold uppercase text-muted">Dispositivos</span>
        <Button variant="ghost" size="sm" onClick={() => refetch()} title="Refrescar">
          <RefreshCw size={12} />
        </Button>
      </div>
      <div className="px-2 py-1.5 border-b border-line">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar abonado o IP..."
            className="bg-bg border border-line rounded pl-6 pr-6 py-1 text-xs text-text placeholder-muted focus:outline-none focus:border-accent w-full"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted hover:text-text"
              title="Limpiar"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-auto py-1">
        {isLoading && (
          <div className="flex items-center justify-center p-4"><Spinner /></div>
        )}
        {!isLoading && devices.length === 0 && (
          <p className="text-muted text-xs text-center mt-4 px-2">
            No hay dispositivos.<br />Agregalos en la pagina "Dispositivos".
          </p>
        )}
        {!isLoading && devices.length > 0 && filteredDevices.length === 0 && (
          <p className="text-muted text-xs text-center mt-4 px-2">
            Sin resultados para "{query}".
          </p>
        )}
        {filteredDevices.map((d) => (
          <DeviceNode
            key={d.id}
            device={d}
            channels={channelsByDevice[d.id] || []}
            expanded={!!expanded[d.id]}
            onToggle={() => toggle(d.id)}
            onSync={() => handleSync(d.id)}
            onTest={() => handleTest(d.id)}
          />
        ))}
      </div>
    </div>
  );
}
